import React from "react";
import { logErrorToGA } from "./Logger";

type State = {
  error: any;
  errorInfo: any;
  refId: string | null;
};

function makeRefId(): string {
  // Short, easy-to-quote identifier (timestamp + 4 random hex chars)
  const ts = new Date().toISOString().replace(/[-:.TZ]/g, "").slice(2, 14); // YYMMDDhhmmss
  const rand = Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0");
  return `${ts}-${rand}`;
}

export class ErrorBoundary extends React.Component<{}, State> {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, refId: null };
  }

  componentDidCatch(error, errorInfo) {
    if (error.name === "ChunkLoadError") {
      window.location.reload();
      return;
    }

    const refId = makeRefId();

    // Catch errors in any components below and re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo,
      refId,
    });

    // Tag the error with the ref id so it's discoverable in GA / Sentry / console
    try {
      // eslint-disable-next-line no-console
      console.error(`[ErrorBoundary refId=${refId}]`, error, errorInfo);
    } catch {}

    logErrorToGA(error, true);
  }

  render() {
    if (this.state.errorInfo) {
      const { refId } = this.state;
      const mailtoBody = encodeURIComponent(
        `Hi,\n\nI hit an error in DocuClipper.\n\nReference: ${refId}\nURL: ${window.location.href}\n\nWhat I was doing:\n`
      );
      return (
        <div className="p-4 text-center" style={{ maxWidth: 520, margin: "4rem auto" }}>
          <h2>Something went wrong</h2>
          <p className="text-muted mb-3">
            Try{" "}
            <a href="#" onClick={(e) => { e.preventDefault(); window.location.reload(); }}>
              refreshing the page
            </a>
            . If the problem continues, email{" "}
            <a href={`mailto:support@docuclipper.com?subject=DocuClipper%20error%20${refId}&body=${mailtoBody}`}>
              support@docuclipper.com
            </a>{" "}
            and include the reference below so we can find your error in the logs.
          </p>
          {refId && (
            <p className="mb-0">
              <small className="text-muted">Reference:</small>{" "}
              <code style={{ userSelect: "all" }}>{refId}</code>
            </p>
          )}
        </div>
      );
    }
    // Normally, just render children
    return this.props.children;
  }
}
